// NavBar.jsx
import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import {useBranding} from "../../context/BrandingContext";
import {alphaFromHex} from "../../util/colors";

export default function NavBar({ navBarElem }) {
    const links = navBarElem?.links || [];

    const colors = useThemeColors();
    const {ready} = useBranding();
    const primary = colors.primary;
    const textPrimary =
        GlobalStyles.colors.woodsmoke || "#14151a";

    const styles = useMemo(() => {
        return {
            wrap: {
                padding: "14px 14px 10px",
                marginBottom: 10,
            },
            panel: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
                background: "#fff", // keep light
            },
            top: {
                padding: "12px 14px",
                // ✅ Primary-based soft gradient
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(primary, 0.50)},
                    ${alphaFromHex(primary, 0.0)}
                )`,
                // borderBottom: `1px solid ${alphaFromHex(primary, 0.25)}`,
            },
            row: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 240,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: textPrimary,
                background: alphaFromHex(primary, 0.25),
                border: `1px solid ${alphaFromHex(primary, 0.45)}`,
                textTransform: "uppercase",
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 18,
                fontWeight: 900,
                color: textPrimary,
            },
            crumbsWrap: {
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "flex-end",
                margin: 0,
                padding: 0,
                listStyle: "none",
            },
            crumb: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "rgba(20,21,26,0.80)",
                fontWeight: 800,
                fontSize: 12.5,
                lineHeight: 1,
            },
            crumbLink: {
                color: "rgba(20,21,26,0.86)",
                textDecoration: "none",
            },
            // ✅ Active breadcrumb uses primary theme
            crumbActive: {
                background: alphaFromHex(primary, 0.25),
                border: `1px solid ${alphaFromHex(primary, 0.6)}`,
                color: textPrimary,
            },
            sep: {
                opacity: 0.45,
                fontWeight: 900,
            },
        };
    }, [primary, textPrimary]);

    if (!ready) return null;

    return (
        <section className="content-header" style={styles.wrap}>
            <div className="container-fluid p-0">
                <div style={styles.panel}>
                    <div style={styles.top}>
                        <div style={styles.row}>
                            {/* Title */}
                            <div style={styles.titleWrap}>
                                <h1 style={styles.title}>
                                    {navBarElem?.pageTitle}
                                </h1>
                            </div>

                            {/* Breadcrumbs */}
                            <ol style={styles.crumbsWrap} aria-label="breadcrumb">
                                {links.map((link, i) => {
                                    const key = `${i}-${link.pageTitle}`;
                                    const isLast = !link.navLink;

                                    return (
                                        <li
                                            key={key}
                                            style={{
                                                ...styles.crumb,
                                                ...(isLast
                                                    ? styles.crumbActive
                                                    : null),
                                            }}
                                            {...(isLast
                                                ? { "aria-current": "page" }
                                                : {})}
                                        >
                                            {i > 0 ? (
                                                <span style={styles.sep}>/</span>
                                            ) : null}

                                            {isLast ? (
                                                <span>{link.pageTitle}</span>
                                            ) : (
                                                <NavLink
                                                    to={link.navLink}
                                                    state={link.state}
                                                    end
                                                    style={styles.crumbLink}
                                                >
                                                    {link.pageTitle}
                                                </NavLink>
                                            )}
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}